import { asKgRateWorkingRow, num, normalizedMaterialName } from './concreteEstimatorRateCalc';

/** Rate working always uses recipe kg/m³ for 1 m³ (not total project volume). */
export const RATE_WORKING_BASIS_M3 = 1;

export function effectiveMixVolume(_volumeM3) {
    return RATE_WORKING_BASIS_M3;
}

function sandTypeFromName(name) {
    const n = normalizedMaterialName(name);
    if (!n) return null;
    if (n.includes('rsand') || n.includes('riversand')) return 'rsand';
    if (n.includes('msand')) return 'msand';
    if (n.includes('csand')) return 'csand';
    return null;
}

function sandTypeFromLabel(label) {
    const n = normalizedMaterialName(label);
    if (!n) return null;
    if (n.includes('rsand') || n.includes('riversand')) return 'rsand';
    if (n.includes('msand')) return 'msand';
    if (n.includes('csand') || n.includes('crushsand')) return 'csand';
    return null;
}

function isRiverSandProduct(productName) {
    return sandTypeFromName(productName) === 'rsand';
}

function isFineAggregateProduct(productName) {
    return Boolean(sandTypeFromName(productName));
}

function labelMatchesProduct(labelNorm, productName) {
    const name = normalizedMaterialName(productName);
    if (!labelNorm || !name) return false;
    if (labelNorm === name) return true;

    const productSand = sandTypeFromName(name);
    const labelSand = sandTypeFromLabel(labelNorm);
    if (productSand) {
        return Boolean(labelSand && productSand === labelSand);
    }

    return labelNorm.includes(name) || name.includes(labelNorm);
}

function mixFieldForProduct(productName, mix = {}) {
    const name = normalizedMaterialName(productName);
    if (!name) return null;

    if (isRiverSandProduct(productName)) {
        return null;
    }

    const labelFields = [
        ['cem1_label', 'cem1'],
        ['cem2_label', 'cem2'],
        ['cem3_label', 'cem3'],
        ['cem4_label', 'cem4'],
        ['cem5_label', 'cem5'],
        ['agg1_label', 'agg1'],
        ['agg2_label', 'agg2'],
        ['agg3_label', 'agg3'],
        ['agg4_label', 'agg4'],
        ['agg5_label', 'agg5'],
    ];
    for (const [labelKey, fieldKey] of labelFields) {
        const label = mix[labelKey];
        if (!label) continue;
        const labelNorm = normalizedMaterialName(label);
        if (labelMatchesProduct(labelNorm, productName)) {
            return fieldKey;
        }
    }

    if (name.includes('flyash')) return 'cem2';
    if (name.includes('ggbs') || name.includes('slag')) return 'cem3';
    if (name.includes('cement') || name.includes('opc') || name.includes('ppc')) return 'cem1';
    if (name.includes('20mm')) return 'agg1';
    if (name.includes('12mm')) return 'agg2';
    if (name.includes('msand')) return 'agg4';
    if (name.includes('csand')) return 'agg5';
    if (name.includes('admix')) return 'adm1';
    return null;
}

function productQtyKg(productName, mix, volumeM3) {
    const field = mixFieldForProduct(productName, mix);
    if (!field) return 0;
    const kgPerM3 = num(mix[field]);
    if (kgPerM3 <= 0) return 0;
    return kgPerM3 * effectiveMixVolume(volumeM3);
}

export function applyDesignMixToMaterials(materials = [], mix = {}, volumeM3 = 0) {
    return materials.map((row) => {
        const productName = row.product_name || '';
        if (isRiverSandProduct(productName)) {
            return row;
        }

        const kgQty = productQtyKg(productName, mix, volumeM3);
        if (kgQty > 0) {
            return asKgRateWorkingRow({
                ...row,
                quantity: Math.round(kgQty * 1000) / 1000,
            }, row.unit);
        }

        if (isFineAggregateProduct(productName)) {
            return asKgRateWorkingRow({
                ...row,
                quantity: 0,
            }, row.unit);
        }

        return row;
    });
}

export function uniqueGradesFromLines(lines = []) {
    return [...new Set(
        lines
            .map((line) => line.concrete_grade || '')
            .filter(Boolean),
    )];
}

export function mixRecipeSnapshot(mixRow) {
    if (!mixRow?.id) return null;
    const grade = mixRow.grade || '';
    const site = mixRow.site || mixRow.customer || '';
    return {
        id: mixRow.id,
        label: mixRow.label || `${grade} · ${site}`.trim(),
        customer: mixRow.customer || '',
        site: mixRow.site || '',
        grade,
        work_schedule_no: mixRow.work_schedule_no || '',
        work_schedule_date: mixRow.work_schedule_date || '',
        mix_summary: mixRow.mix_summary || '',
        total_cem: mixRow.total_cem,
        water_cem: mixRow.water_cem,
        density: mixRow.density,
        cem1: mixRow.cem1,
        cem2: mixRow.cem2,
        cem3: mixRow.cem3,
        cem4: mixRow.cem4,
        cem5: mixRow.cem5,
        agg1: mixRow.agg1,
        agg2: mixRow.agg2,
        agg3: mixRow.agg3,
        agg4: mixRow.agg4,
        agg5: mixRow.agg5,
        adm1: mixRow.adm1,
        adm2: mixRow.adm2,
        others: mixRow.others,
    };
}

export function recipeDisplayRows(recipe) {
    if (!recipe) return [];
    return [
        { label: 'Cement / Silo 1', value: recipe.cem1 },
        { label: 'Flyash / Silo 2', value: recipe.cem2 },
        { label: 'Silo 3', value: recipe.cem3 },
        { label: '20mm / Bin 1', value: recipe.agg1 },
        { label: '12mm / Bin 2', value: recipe.agg2 },
        { label: 'RSAND / Bin 3', value: recipe.agg3 },
        { label: 'MSAND / Bin 4', value: recipe.agg4 },
        { label: 'CSAND / Bin 5', value: recipe.agg5 },
        { label: 'Admixture 1', value: recipe.adm1 },
        { label: 'Admixture 2', value: recipe.adm2 },
    ].filter((row) => Number(row.value) > 0);
}

export function scopeLabel(scope) {
    if (scope === 'site') return 'Customer / Site mix';
    if (scope === 'plant_grade') return 'All mixes for grade';
    if (scope === 'site_all_grades') return 'All grades for customer / site';
    if (scope === 'plant_all') return 'All plant mixes';
    return scope || '';
}

export function buildMixSummary(row = {}) {
    const totalCem = Number(row.total_cem || 0);
    const sand = Number(row.agg3 || 0) + Number(row.agg4 || 0) + Number(row.agg5 || 0);
    return `Cem ${totalCem.toFixed(0)} kg/m³ · 20mm ${Number(row.agg1 || 0).toFixed(0)} · 12mm ${Number(row.agg2 || 0).toFixed(0)} · Sand ${sand.toFixed(0)} · Density ${Number(row.density || 0).toFixed(0)}`;
}

/** Map QC /design_mix/ list rows to estimator picker options. */
export function mapDesignMixListToOptions(list = []) {
    return (list || [])
        .filter((row) => row?.id != null)
        .map((row) => ({
            id: row.id,
            customer: row.customer || '',
            site: row.site || '',
            grade: row.grade || '',
            work_schedule_no: row.work_schedule_no || '',
            work_schedule_date: row.work_schedule_date || '',
            sales_order_no: row.sales_order_no || '',
            conc_structure: row.conc_structure || '',
            delivery_mode: row.delivery_mode || '',
            plant: row.plant || '',
            density: row.density,
            total_cem: row.total_cem,
            water_cem: row.water_cem,
            mix_summary: row.mix_summary || buildMixSummary(row),
            scope: row.scope || 'plant_all',
            cem1: row.cem1,
            cem2: row.cem2,
            cem3: row.cem3,
            cem4: row.cem4,
            cem5: row.cem5,
            agg1: row.agg1,
            agg2: row.agg2,
            agg3: row.agg3,
            agg4: row.agg4,
            agg5: row.agg5,
            adm1: row.adm1,
            adm2: row.adm2,
            others: row.others,
        }));
}

export function gradesMatch(mixGrade, selectedGrade) {
    const left = String(mixGrade || '').toLowerCase().trim();
    const right = String(selectedGrade || '').toLowerCase().trim();
    if (!left || !right) return false;
    return left.includes(right) || right.includes(left);
}

export function filterDesignMixOptions(options = [], gradeFilter, searchText) {
    let rows = options;
    if (gradeFilter) {
        rows = rows.filter((row) => gradesMatch(row.grade, gradeFilter));
    }
    if (searchText) {
        const token = String(searchText).trim().toLowerCase();
        if (token) {
            rows = rows.filter((row) => (
                [
                    row.customer,
                    row.site,
                    row.grade,
                    row.work_schedule_no,
                    row.sales_order_no,
                    row.conc_structure,
                    row.mix_summary,
                ]
                    .map((value) => String(value || '').toLowerCase())
                    .some((value) => value.includes(token))
            ));
        }
    }
    return rows;
}
